/**
 * Shared navigation configuration for the responsive redesign.
 * Matches the deployed app's menu items exactly.
 *
 * Localization (Phase 2): items carry a typed `labelKey` into the
 * `navigation` catalog namespace; LIVE shell components (TopNav, Sidebar,
 * Drawer) translate it AT RENDER TIME via t(`items.${labelKey}`) so labels
 * stay reactive to runtime language switching — translated strings are
 * never evaluated once at module import. The English `label` field remains
 * for legacy/dead consumers (GameScreenSidebar, MobileDrawer) that are not
 * part of the live shell and stay English like other unmigrated surfaces.
 */
import type navigationEn from '../i18n/locales/en/navigation.json';

export type NavLabelKey = keyof (typeof navigationEn)['items'];

export interface NavItem {
  path:   string;
  /** Legacy English label — used only by dead/unrouted consumers. */
  label:  string;
  /** Typed key into the `navigation` catalog's `items` group. */
  labelKey: NavLabelKey;
  /** Icon key — resolved to an SVG by NavIcon.tsx */
  icon:   NavIconKey;
  /** If true, item is not yet launched — show a "Soon" badge */
  soon?:  boolean;
}

export type NavIconKey =
  | 'home'
  | 'play'
  | 'play-ai'
  | 'profile'
  | 'stats'
  | 'settings'
  | 'friends'
  | 'shop'
  | 'leaderboard'
  | 'bot'
  | 'rules'
  | 'messages'
  | 'achievements';

/**
 * Primary nav items — same order as the deployed desktop sidebar and mobile drawer.
 * Home · Play Online · Play vs AI · Friends · Stats · Leaderboard · Settings
 *
 * Settings V1: the old trailing "Profile → /profile" entry became
 * "Settings → /settings" — /settings is the ONE canonical identity for the
 * unified Profile/Settings screen (its active state matches directly; the
 * /profile route survives only as a replace-redirect for old bookmarks).
 * The former separate pinned Settings item was folded in here so no menu
 * carries two navigation identities for the same screen.
 */
export const NAV_ITEMS: NavItem[] = [
  { path: '/home',        label: 'Home',        labelKey: 'home',        icon: 'home'        },
  { path: '/play/online', label: 'Play Online',  labelKey: 'playOnline',  icon: 'play'        },
  { path: '/play/ai',     label: 'Play vs AI',   labelKey: 'playVsAi',    icon: 'play-ai' },
  { path: '/friends',     label: 'Friends',      labelKey: 'friends',     icon: 'friends'             },
  { path: '/stats',       label: 'Stats',        labelKey: 'stats',       icon: 'stats'       },
  { path: '/leaderboard', label: 'Leaderboard',  labelKey: 'leaderboard', icon: 'leaderboard' },
  { path: '/settings',    label: 'Settings',     labelKey: 'settings',    icon: 'settings'    },
];

/**
 * Design System V2 sidebar nav items — matches the approved Figma sidebar
 * (Landing / Get Started / Home / Leaderboard / Friends screenshots).
 * Distinct from NAV_ITEMS (the live TopNav/Drawer set) — kept separate so
 * migrating other authenticated routes to the new Sidebar is a deliberate
 * future decision, not a side effect of this change.
 */
export const SIDEBAR_NAV_ITEMS: NavItem[] = [
  { path: '/play/online', label: 'Play Online', labelKey: 'playOnline',   icon: 'play' },
  { path: '/play/ai',     label: 'Play Bots',    labelKey: 'playBots',     icon: 'bot' },
  { path: '/leaderboard', label: 'Leaderboard',  labelKey: 'leaderboard',  icon: 'leaderboard' },
  { path: '/achievements', label: 'Achievements', labelKey: 'achievements', icon: 'achievements' },
  { path: '/friends',     label: 'Friends',      labelKey: 'friends',      icon: 'friends' },
  { path: '/messages',    label: 'Messages',     labelKey: 'messages',     icon: 'messages' },
  { path: '/rules',       label: 'Rules',        labelKey: 'rules',        icon: 'rules' },
  { path: '/settings',    label: 'Settings',     labelKey: 'settings',     icon: 'settings' },
];
